import { useEffect, useState } from "react";
import { fetchCityBySearchParams } from "services/api/city/cityService";
import { fetchSchoolBySearchParams } from "services/api/school/schoolService";
import { useAuth } from "stores/auth/auth-context";
import { useRouter } from "next/router";

type Option = { id: number; name: string; value: string };

export const useAddLocation = (queries: { city: string; school: string }) => {
  const [cityOptions, setCityOptions] = useState<Option[]>([]);
  const [schoolOptions, setSchoolOptions] = useState<Option[]>([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [schoolLoading, setSchoolLoading] = useState(false);
  const {
    user: { userData, setUserData }
  } = useAuth();
  const router = useRouter();

  const primaryUser = userData.primary_user;

  useEffect(() => {
    if (userData.primary_user.profile_state === "COMPLETE") {
      router.push("/bimbel");
    }
  }, [userData.primary_user]);

  useEffect(() => {
    const fetchCities = async () => {
      if (!queries.city) {
        setCityOptions([]);
        return;
      }
      try {
        setCityLoading(true);
        const res = await fetchCityBySearchParams(queries.city.trim());
        setCityOptions(
          res.data.map((d) => ({ id: d.id, name: d.name, value: d.name }))
        );
      } catch (err) {
        console.error(err);
        setCityOptions([]);
      } finally {
        setCityLoading(false);
      }
    };

    fetchCities();
  }, [queries.city]);

  useEffect(() => {
    const fetchSchools = async () => {
      if (!queries.school) {
        setSchoolOptions([]);
        return;
      }
      try {
        setSchoolLoading(true);
        const res = await fetchSchoolBySearchParams(queries.school.trim());
        setSchoolOptions(
          res.data.map((d) => ({ id: d.id, name: d.name, value: d.name }))
        );
      } catch (err) {
        console.error(err);
        setSchoolOptions([]);
      } finally {
        setSchoolLoading(false);
      }
    };

    fetchSchools();
  }, [queries.school]);

  const isCustomCity =
    !cityLoading &&
    queries.city.trim() !== "" &&
    !cityOptions.some((opt) =>
      opt.name.trim().toLowerCase().includes(queries.city.trim().toLowerCase())
    );

  const isCustomSchool =
    !schoolLoading &&
    queries.school.trim() !== "" &&
    !schoolOptions.some((opt) =>
      opt.name
        .trim()
        .toLowerCase()
        .includes(queries.school.trim().toLowerCase())
    );

  const isFormValid =
    queries.city.trim() !== "" && queries.school.trim() !== "";

  const updateLocalUserData = (city: string, school: string) => {
    setUserData((prevData) => ({
      ...prevData,
      primary_user: {
        ...prevData.primary_user,
        ...(city && { city }),
        ...(school && { school })
      },
      student_users:
        prevData.student_users?.map((student) =>
          student.id === primaryUser.id
            ? {
                ...student,
                ...(city && { city }),
                ...(school && { school })
              }
            : student
        ) || []
    }));
  };

  return {
    cityOptions,
    schoolOptions,
    isCustomCity,
    isCustomSchool,
    isFormValid,
    primaryUser,
    updateLocalUserData
  };
};
